/**
 * Group Service
 *
 * Handles distribution group management operations with real API integration.
 * Supports both email members and nested child groups.
 */

import apiClient from './api';

/**
 * Get list of groups with search and pagination
 */
export const getGroups = async (params = {}) => {
  try {
    const { search = '', page = 1, limit = 10 } = params;

    const response = await apiClient.get('/groups', {
      params: { search, page, page_size: limit }
    });

    const apiData = response.data?.data || response.data;
    const groupList = apiData?.groups || [];
    const groups = groupList.map(group => ({
      id: group.ID,
      name: group.NAME,
      description: group.DESCRIPTION || '',
      emailCount: group.EMAIL_COUNT || 0,
      childGroupCount: group.CHILD_GROUP_COUNT || 0,
      createdDate: (group.CREATED_AT || '')?.split('T')[0] || '',
      isActive: group.IS_ACTIVE
    }));

    return {
      data: groups,
      pagination: {
        page: apiData?.pagination?.page || page,
        limit: apiData?.pagination?.page_size || limit,
        total: apiData?.pagination?.total || 0,
        totalPages: apiData?.pagination?.total_pages || 1
      }
    };
  } catch (error) {
    console.error('Failed to fetch groups:', error);
    throw error;
  }
};

/**
 * Get group by ID (includes emails and child groups)
 */
export const getGroupById = async (groupId) => {
  try {
    const response = await apiClient.get(`/groups/${groupId}`);
    const apiData = response.data?.data || response.data;

    return {
      id: apiData.ID,
      name: apiData.NAME,
      description: apiData.DESCRIPTION || '',
      emailCount: apiData.EMAIL_COUNT || 0,
      childGroupCount: apiData.CHILD_GROUP_COUNT || 0,
      createdDate: (apiData.CREATED_AT || '')?.split('T')[0] || '',
      isActive: apiData.IS_ACTIVE,
      emails: (apiData.EMAILS || []).map(e => ({
        id: e.ID,
        email: e.EMAIL_ADDRESS,
        name: e.NAME || '',
        verificationStatus: (e.VERIFICATION_STATUS || 'pending').toLowerCase()
      })),
      childGroups: (apiData.CHILD_GROUPS || []).map(g => ({
        id: g.ID,
        name: g.NAME,
        description: g.DESCRIPTION || '',
        emailCount: g.EMAIL_COUNT || 0
      }))
    };
  } catch (error) {
    console.error('Failed to fetch group:', error);
    throw error;
  }
};

/**
 * Create new group
 */
export const createGroup = async (groupData) => {
  try {
    const config = {};
    if (groupData.teamId) {
      config.headers = { 'X-Active-Team-Id': groupData.teamId };
    }

    const response = await apiClient.post('/groups', {
      group_name: groupData.name,
      description: groupData.description
    }, config);

    const apiData = response.data?.data || response.data;

    return {
      data: {
        id: apiData.id || apiData.group_id,
        name: apiData.group_name,
        description: apiData.description || '',
        emailCount: 0,
        childGroupCount: 0,
        createdDate: new Date().toISOString().split('T')[0]
      }
    };
  } catch (error) {
    console.error('Failed to create group:', error);
    throw error;
  }
};

/**
 * Update group
 */
export const updateGroup = async (groupId, groupData) => {
  try {
    await apiClient.put(`/groups/${groupId}`, {
      group_name: groupData.name,
      description: groupData.description
    });

    return {
      data: {
        id: groupId,
        name: groupData.name,
        description: groupData.description
      }
    };
  } catch (error) {
    console.error('Failed to update group:', error);
    throw error;
  }
};

/**
 * Delete group
 */
export const deleteGroup = async (groupId) => {
  try {
    await apiClient.delete(`/groups/${groupId}`);
    return { success: true };
  } catch (error) {
    console.error('Failed to delete group:', error);
    throw error;
  }
};

/**
 * Get emails in a group
 */
export const getGroupEmails = async (groupId) => {
  try {
    const response = await apiClient.get(`/groups/${groupId}/emails`);
    const apiData = response.data?.data || response.data;

    return (apiData.emails || []).map(e => ({
      id: e.ID,
      email: e.EMAIL_ADDRESS,
      name: e.NAME || '',
      verificationStatus: (e.VERIFICATION_STATUS || 'pending').toLowerCase(),
      addedAt: (e.ADDED_AT || '')?.split('T')[0] || ''
    }));
  } catch (error) {
    console.error('Failed to fetch group emails:', error);
    throw error;
  }
};

/**
 * Add emails to group
 */
export const addEmailsToGroup = async (groupId, emailIds) => {
  try {
    const response = await apiClient.post(`/groups/${groupId}/emails`, {
      email_ids: emailIds
    });
    return response.data?.data || response.data;
  } catch (error) {
    console.error('Failed to add emails to group:', error);
    throw error;
  }
};

/**
 * Remove email from group
 */
export const removeEmailFromGroup = async (groupId, emailId) => {
  try {
    await apiClient.delete(`/groups/${groupId}/emails/${emailId}`);
    return { success: true };
  } catch (error) {
    console.error('Failed to remove email from group:', error);
    throw error;
  }
};

/**
 * Get child groups of a group
 */
export const getGroupChildGroups = async (groupId) => {
  try {
    const response = await apiClient.get(`/groups/${groupId}/groups`);
    const apiData = response.data?.data || response.data;

    return (apiData.groups || []).map(g => ({
      id: g.ID,
      name: g.NAME,
      description: g.DESCRIPTION || '',
      emailCount: g.EMAIL_COUNT || 0,
      addedAt: (g.ADDED_AT || '')?.split('T')[0] || ''
    }));
  } catch (error) {
    console.error('Failed to fetch child groups:', error);
    throw error;
  }
};

/**
 * Add child groups to a group
 */
export const addChildGroupsToGroup = async (groupId, childGroupIds) => {
  try {
    const response = await apiClient.post(`/groups/${groupId}/groups`, {
      group_ids: childGroupIds
    });
    return response.data?.data || response.data;
  } catch (error) {
    console.error('Failed to add child groups:', error);
    throw error;
  }
};

/**
 * Remove child group from a group
 */
export const removeChildGroupFromGroup = async (groupId, childGroupId) => {
  try {
    await apiClient.delete(`/groups/${groupId}/groups/${childGroupId}`);
    return { success: true };
  } catch (error) {
    console.error('Failed to remove child group:', error);
    throw error;
  }
};
